/**
 * Quickbar — horizontal strip of pinned favourite pages.
 * Shown below the topbar only when the user has at least one favourite.
 * × buttons only appear in edit mode (toggled by the pencil icon) to prevent accidental removal.
 */

import { useState, useEffect } from 'react'

import { cn } from '@elsapiens/utils'
import { NavLink, useLocation } from 'react-router-dom'

import { useFavorites } from '../../stores/favorites-store'

interface FavoriteItem {
  id: string
  label: string
  path: string
}

interface QuickbarItemProps {
  item: FavoriteItem
  isEditing: boolean
  onRemove: (id: string) => void
  onClickItem: () => void
}

function QuickbarItem({ item, isEditing, onRemove, onClickItem }: QuickbarItemProps): JSX.Element {
  return (
    <div key={item.id} className="flex-shrink-0 flex items-center">
      <NavLink
        className={({ isActive }) =>
          cn(
            'flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap',
            isActive
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground hover:bg-muted/70',
          )
        }
        to={item.path}
        onClick={onClickItem}
      >
        {item.label}
      </NavLink>
      {isEditing && (
        <button
          aria-label={`Remove ${item.label} from quickbar`}
          className="ml-0.5 p-0.5 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
          onClick={() => onRemove(item.id)}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} />
          </svg>
        </button>
      )}
    </div>
  )
}

function EditToggleButton({ isEditing, onToggle }: { isEditing: boolean; onToggle: () => void }): JSX.Element {
  return (
    <button
      aria-label={isEditing ? 'Done editing quickbar' : 'Edit quickbar'}
      className={cn(
        'flex-shrink-0 ml-1 p-1 rounded-md text-xs transition-colors',
        isEditing
          ? 'text-primary font-medium'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted',
      )}
      title={isEditing ? 'Done' : 'Edit quickbar'}
      onClick={onToggle}
    >
      {isEditing ? (
        <span className="text-[10px] font-semibold">Done</span>
      ) : (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
        </svg>
      )}
    </button>
  )
}

export function Quickbar(): React.ReactElement | null {
  const { favorites, remove } = useFavorites()
  const [isEditing, setIsEditing] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setIsEditing(false)
  }, [pathname])

  if (favorites.length === 0) { return null }

  return (
    <div className="border-b border-border bg-surface/80 backdrop-blur-sm el-px-lg">
      <div className="flex items-center gap-1 el-px-md el-py-xs overflow-x-auto scrollbar-none">
        <span className="flex-shrink-0 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-1 select-none">
          Quick
        </span>
        {favorites.map(item => (
          <QuickbarItem
            key={item.id}
            isEditing={isEditing}
            item={item}
            onClickItem={() => setIsEditing(false)}
            onRemove={remove}
          />
        ))}
        <EditToggleButton isEditing={isEditing} onToggle={() => setIsEditing(v => !v)} />
      </div>
    </div>
  )
}
